import HandlerError from 'errors/HandlerError';
import ServerError from 'errors/ServerError';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import SM from 'services/ServiceManager';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import handlerRequestCanceling from 'utils/handlerRequestCanceling';

// --- Interfaces ---

export interface Question {
    Id: string | number;
    Type: 'MultiAnswer' | 'SingleAnswer' | 'FreeText' | string;
    MinimumNumberOfAnswers: number;
    MaximumNumberOfAnswers: number;
    [key: string]: any;
}

export interface Step {
    Questions: Question[];
}

export interface Group {
    Id: string | number;
    Steps: Step[];
}

export interface QuestionnaireData {
    Groups: Group[];
    [key: string]: any;
}

interface UseQuestionnaireOptions {
    clientId?: string | number;
    productId?: number;
    questionnaireTypeId?: number;
}

// --- Query keys (kept in sync with the `getData_${api}` keying used below) ---

export const QUESTIONNAIRE_QUERY_KEYS = [
    'getData_getQuestionnaireAnswers',
    'getData_getQuestionnaireAnswersByContainer',
    'getData_filterQuestionnaires',
];

// --- Cache config ---

const QUESTIONNAIRE_STALE_TIME = 5 * 60 * 1000;
const QUESTIONNAIRE_GC_TIME = 10 * 60 * 1000;

// --- Hook ---

export const useQuestionnaire = (options: UseQuestionnaireOptions = {}) => {
    const {
        clientId,
        productId: productIdOption,
        questionnaireTypeId: questionnaireTypeIdOption = 1,
    } = options;

    const { t, i18n: { language } } = useTranslation();
    const queryClient = useQueryClient();

    const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData | null>(null);
    const [error, setError] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const getQuestionnaire = useCallback(async ({
        productId = productIdOption,
        questionnaireTypeId = questionnaireTypeIdOption,
        id,
        adapter = (data: any) => data,
        isContainer = false,
    }: {
        productId?: number;
        questionnaireTypeId?: number;
        id?: string | number;
        adapter?: (data: any) => any;
        isContainer?: boolean;
    } = {}) => {
        if (!productId || !questionnaireTypeId || productId < 1 || questionnaireTypeId < 1) {
            return null;
        }
        setError(null);
        setIsLoading(true);

        const params = {
            Language: language,
            ProductIds: [productId],
            ContactTypeId: 1,
            QuestionnaireTypeId: questionnaireTypeId,
        };
        const answersApi = isContainer ? 'getQuestionnaireAnswersByContainer' : 'getQuestionnaireAnswers';

        try {
            // Cached read of the saved answers (dedupes and reuses within staleTime).
            let response: any = await queryClient.fetchQuery({
                queryKey: [`getData_${answersApi}`, id],
                queryFn: () => SM.customInvestmentService(answersApi, [{ id }]),
                staleTime: QUESTIONNAIRE_STALE_TIME,
                gcTime: QUESTIONNAIRE_GC_TIME,
                retry: false,
            });

            if (!response?.data) {
                // Fallback: cached read of the questionnaire template.
                const complianceResponse: any = await queryClient.fetchQuery({
                    queryKey: ['getData_filterQuestionnaires', params],
                    queryFn: () => SM.compliance('filterQuestionnaires', [params]),
                    staleTime: QUESTIONNAIRE_STALE_TIME,
                    gcTime: QUESTIONNAIRE_GC_TIME,
                    retry: false,
                });

                response = { data: complianceResponse?.data?.Results?.[0] };
            }

            const adaptedData = adapter(response?.data);

            setQuestionnaireData(adaptedData);

            return adaptedData;
        } catch (err: any) {
            handlerRequestCanceling(
                HandlerError({
                    setError: (val) => setError(val),
                    setLoading: (val) => setIsLoading(val),
                }),
            )(err);

            return null;
        } finally {
            setIsLoading(false);
        }
    }, [language, productIdOption, questionnaireTypeIdOption, queryClient]);

    const validateQuestionnaire = useCallback(({
        data = questionnaireData,
        groupId,
        answers = {},
        errorMessage,
    }: {
        data?: QuestionnaireData | null;
        groupId?: string | number;
        answers?: Record<string, any>;
        errorMessage?: string;
    } = {}) => {
        const errors: Record<string, string> = {};

        const groupToValidate = groupId
            ? (data?.Groups || []).filter(({ Id }) => Id === groupId)
            : (data?.Groups || []);

        const answersToValidate = groupId
            ? (answers[groupId] || {})
            : Object.values(answers || {}).reduce((acc, group) => ({ ...acc, ...group }), {});

        // Flatten questions safely
        const questions: Question[] = groupToValidate.flatMap(group => (group.Steps || []).flatMap(step => step.Questions || []));

        questions.forEach((question) => {
            const {
                Id, Type, MinimumNumberOfAnswers, MaximumNumberOfAnswers,
            } = question;
            const value = answersToValidate[Id];
            const isRequired = Type === 'MultiAnswer' ? MinimumNumberOfAnswers > 0 : true;

            const hasAnswer = Object.keys(answersToValidate).includes(Id.toString());

            if (!hasAnswer && isRequired) {
                errors[Id] = errorMessage || t('validation.mandatoryField');
            }
            if (value?.length < MinimumNumberOfAnswers) {
                errors[Id] = `${t('validation.questionnaire.minimumNumberOfAnswers')}: ${MinimumNumberOfAnswers}`;
            }
            if (value?.length > MaximumNumberOfAnswers) {
                errors[Id] = `${t('validation.questionnaire.maximumNumberOfAnswers')}: ${MaximumNumberOfAnswers}`;
            }
        });

        return { isValid: Object.keys(errors).length === 0, errors };
    }, [questionnaireData, t]);

    const postMutation = useGenericAPIMutation();
    const calculateMutation = useGenericAPIMutation();

    const postQuestionnaire = useCallback(async ({
        answers,
        id,
        isContainer = false,
    }: {
        answers: any;
        id: string | number;
        isContainer?: boolean;
    }) => {
        const postApi = isContainer ? 'postQuestionnaireAnswersByContainer' : 'postQuestionnaireAnswers';
        const answersApi = isContainer ? 'getQuestionnaireAnswersByContainer' : 'getQuestionnaireAnswers';

        try {
            const response: any = await postMutation.mutateAsync({
                service: 'customInvestmentService',
                API: postApi,
                payload: [{ id, data: answers }],
                // Refresh the cached answers so the next read reflects the submission.
                invalidateQueryKeys: [`getData_${answersApi}`],
            });

            return response?.data;
        } catch (err: any) {
            throw err.type !== undefined ? err : new ServerError(err);
        }
    }, [postMutation]);

    const calculateRisk = useCallback(async ({
        productId = productIdOption,
    }: {
        productId?: number;
    } = {}) => {
        try {
            const response: any = await calculateMutation.mutateAsync({
                service: 'compliance',
                API: 'calculateRisk',
                payload: [{ contactId: clientId, ProductIds: [productId], language }],
            });

            const calculatedData = response?.data?.CalculatedProductRiskCategories?.[0];

            return {
                ...calculatedData,
                submissionId: response?.data?.SubmissionId,
            };
        } catch (err: any) {
            throw err.type !== undefined ? err : new ServerError(err);
        }
    }, [clientId, language, productIdOption, calculateMutation]);

    return {
        data: questionnaireData,
        error,
        isLoading,
        getQuestionnaire,
        validateQuestionnaire,
        dataPost: postMutation.data ?? null,
        errorPost: postMutation.error ?? null,
        isLoadingPost: postMutation.isPending,
        postQuestionnaire,
        calculateRisk,
    };
};
